// Stridsmelding-komponist (C2): velg type → strukturert skjema → plasser på
// kartet. GNÅ-stylet flytende panel, samme mønster som enhetspaletten.
//
// Logikken som kan feile (tallvalidering, autoutfylling, doktrinær versalisering)
// ligger i EKSPORTERTE, rene funksjoner nederst — ikke inne i klikk-handleren.
// apps/cop har ingen jsdom/happy-dom i workspacet, så det er den eneste måten
// disse reglene kan testes i vitest (samme grep som `lysforhold.ts`).
import {
  REPORT_FIELDS, buildDtg, latLonToMgrs, stridsEmphasis, oppgraderFritekst,
  type ObjektAnker, type ReportType, type StridsSone,
} from '@skade/contracts';
import { forhaandsutfyll, type Kontekst } from '../kontekst.js';
import { KontekstBlokk } from '../ui/kontekstblokk.js';

const TYPES: Array<{ id: ReportType; label: string }> = [
  { id: 'MELDING', label: 'STRIDSMELDING' },
  { id: 'SALUTE', label: 'SALUTE (observasjon)' },
  { id: 'CONTACT', label: 'CONTACT (TIC)' },
  { id: 'SITREP', label: 'SITREP' },
  { id: 'MEDEVAC', label: 'MEDEVAC (9-liner)' },
  { id: 'UXO', label: 'UXO (EOD/9-liner)' },
  { id: 'CBRN', label: 'CBRN-hendelse' },
];

export type ReportSubmit = (
  type: ReportType,
  fields: Record<string, string>,
  coord: [number, number] | null,
  /**
   * Objektet meldingen handler om (UX-funn F4). Udefinert for en vanlig melding —
   * de aller fleste meldinger handler om noe som ikke finnes som objekt ennå.
   */
  omObjekt?: ObjektAnker,
) => void;

export class ReportComposer {
  /** Rot-elementet. Offentlig som i alle andre paneler (`readonly el`), fordi
   * WindowManager må kjenne det for regelen «ett kommunikasjonspanel åpent om
   * gangen» — komponisten henger i <body> og fanges ikke av panel-sveipen. */
  readonly el: HTMLElement;
  private open = false;
  private type: ReportType = 'MELDING';
  private targetCoord: [number, number] | null = null;
  private form!: HTMLElement;
  private blokk!: KontekstBlokk;
  private typeRow!: HTMLElement;
  /**
   * Fri tekst som er OPPGRADERT hit fra «Meld fra»-linja (UX-rapportens Fase 2).
   *
   * Holdes utenfor `inputs` fordi den skal overleve et typebytte: bytter
   * operatøren fra TIC til SALUTE etter at hun har oppgradert, skal ordene hennes
   * FØLGE MED til det nye formatets fritekstfelt. Ellers ville et feilklikk på en
   * chip slettet setningen hun nettopp sa.
   */
  private fritekst = '';
  /**
   * Objektet meldingen skal forankres i (UX-funn F4, HØY).
   *
   * «Å melde om fiendtlig kjøretøy skjer PÅ kjøretøyet, ikke i en løsrevet
   * kanal.» Settes av `openForObjekt` fra sporets/enhetens detaljpanel, og følger
   * meldingen ut — så neste gang noen åpner det samme sporet, står samtalen der.
   */
  private anker: ObjektAnker | null = null;

  /**
   * `hentKontekst` er ÉN kilde til hvem/hvor/når/enhet (`kontekst.ts`).
   * Komponisten leser derfor ikke bruker, klokke eller posisjon selv — den får
   * dem inn, og blir samtidig testbar.
   */
  constructor(private onSubmit: ReportSubmit, private hentKontekst: () => Kontekst) {
    this.el = this.build();
    document.body.appendChild(this.el);
  }

  toggle() { this.targetCoord = null; this.fritekst = ''; this.anker = null; this.open ? this.close() : this.show(); }
  openAt(lon: number, lat: number) { this.targetCoord = [lon, lat]; this.fritekst = ''; this.anker = null; this.show(); }

  /**
   * Åpne en melding FORANKRET i et objekt (UX-funn F4).
   *
   * ⚠ SETTER OGSÅ KARTPUNKTET til objektets posisjon. Uten det måtte operatøren
   * pekt på nytt på det hun nettopp klikket, og observasjonsstedet ville stått
   * tomt for et objekt systemet vet hvor er. Retter hun punktet etterpå, er det
   * hennes valg — ankeret følger uansett med, for meldingen handler fortsatt om
   * det sporet.
   */
  openForObjekt(anker: ObjektAnker, lon: number, lat: number, type: ReportType = 'MELDING') {
    this.anker = anker;
    this.type = type;
    this.targetCoord = [lon, lat];
    this.fritekst = '';
    this.merkTypeknapp();
    this.show();
  }

  /**
   * Åpne med FRI TEKST alt skrevet (UX-rapportens Fase 2, funn F3+F1).
   *
   * «Struktur er en oppgradering, ikke et hinder»: operatøren har skrevet eller
   * sagt setningen sin i «Meld fra»-linja, og trykket en chip. Teksten legges
   * ORDRETT i meldingstypens fritekstfelt — `oppgraderFritekst` i contracts
   * bestemmer hvilket, og for MEDEVAC er svaret at det ikke finnes noe (9-lineren
   * er L1–L9), og da VISES teksten over skjemaet i stedet for å bli plassert et
   * sted den ikke hører.
   */
  openMedFritekst(type: ReportType, tekst: string) {
    this.type = type;
    this.fritekst = tekst.trim();
    this.merkTypeknapp();
    this.show();
  }

  /** Holder type-radens aktive knapp i takt når typen settes utenfra. */
  private merkTypeknapp() {
    for (const b of this.typeRow.querySelectorAll<HTMLElement>('.report-type-btn')) {
      b.classList.toggle('active', b.dataset.type === this.type);
    }
  }
  private show() {
    this.open = true; this.el.classList.add('open');
    this.blokk.oppfrisk(); // fersk DTG + posisjon ved hver åpning
    this.renderForm();
  }
  close() {
    this.open = false; this.targetCoord = null;
    // ⚠ TØMMER OPPGRADERT TEKST. Sto den igjen, ville neste åpning av
    // komponisten — kanskje timer senere, kanskje om en helt annen hendelse —
    // startet med en gammel setning alt utfylt. En melding som bærer forrige
    // hendelses ord er verre enn et tomt skjema.
    this.fritekst = '';
    this.anker = null;
    this.el.classList.remove('open');
  }

  private build(): HTMLElement {
    const p = div('report-composer');
    const head = div('report-head');
    head.append(span('report-title', 'Ny stridsmelding'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.close();
    head.append(x);
    p.append(head);

    const typeRow = div('report-types');
    this.typeRow = typeRow;
    for (const t of TYPES) {
      const b = document.createElement('button');
      b.className = 'report-type-btn' + (t.id === this.type ? ' active' : '');
      // Id-en på elementet, ikke posisjonen i lista: `merkTypeknapp` skal ikke
      // kunne feile fordi noen legger inn en meldingstype midt i TYPES.
      b.dataset.type = t.id;
      b.textContent = t.label;
      b.onclick = () => {
        this.type = t.id;
        for (const el of typeRow.querySelectorAll('.report-type-btn')) el.classList.remove('active');
        b.classList.add('active');
        this.renderForm();
      };
      typeRow.append(b);
    }
    p.append(typeRow);

    // Kontekst-blokka står UTENFOR `this.form`: den bygges én gang og skal ikke
    // rives ned hver gang operatøren bytter meldingstype — da ville avsender og
    // DTG blinket, og et rettet felt blitt slettet av et typebytte.
    // Ledeteksten trekker grensen mot HVOR-feltet i skjemaet (UX-funn 7):
    // avsenderposisjon og observasjonssted er TO ULIKE steder, og uten en
    // forklaring leses to like «Posisjon»-felt som samme opplysning.
    this.blokk = new KontekstBlokk({
      hent: () => this.hentKontekst(),
      posisjonLabel: 'Posisjon (avsender)',
      ledetekst: 'AVSENDER — hvem som melder, hvorfra og når meldingen SENDES. '
        + 'Stedet for selve observasjonen føres i HVOR/Sted-feltet i skjemaet under.',
    });
    p.append(this.blokk.el);

    this.form = div('report-form');
    p.append(this.form);
    return p;
  }

  private renderForm() {
    this.form.innerHTML = '';
    const inputs = new Map<string, HTMLInputElement | HTMLTextAreaElement>();
    // Det systemet selv VET fylles inn nå: `forhaandsutfyll()` — de DOKTRINÆRE
    // feltene fra kontekst (FRA, L1 i MEDEVAC, egen enhet i SITREP …). Felles
    // mekanisme for alle skjema. Verdiene er helt vanlige, redigerbare inndata:
    // operatøren skal kunne rette dem til det han faktisk observerte.
    //
    // TO FELT ER UNNTATT og eies av egne grupper under (UX-funn 7):
    //  * OBSERVASJONSSTEDET (`OBSERVASJONSFELT`) — kartpunktet vinner; uten
    //    punkt forhåndsutfylles det ALDRI stille med avsenderposisjonen uten
    //    merking (se `observasjonsForvalg`).
    //  * OBSERVASJONSTIDEN (`TIDSFELT`) — et OBLIGATORISK valg «Observert nå /
    //    Observert tidligere» i stedet for stille forhåndsutfylling: en DTG som
    //    bare STO der ble aldri rettet, og sendetid ble lest som observasjonstid.
    const k = this.hentKontekst();
    const forvalg = forhaandsutfyll(this.type, k);
    // ── OPPGRADERT FRI TEKST (Fase 2) ──────────────────────────────────────
    // ⚠ LEGGES INN FØR `forvalg` LESES, MEN OVERSKRIVER DEN. Operatørens egne ord
    // veier mer enn en doktrinær forhåndsutfylling: har hun sagt noe, er det det
    // som skal stå. Rekkefølgen er den eneste som gir det resultatet.
    // ── ANKERET, SYNLIG (funn F4) ──────────────────────────────────────────
    // ⚠ MÅ STÅ I SKJEMAET. En melding som stille festes til et spor er en melding
    // operatøren ikke vet hvor havner — og forankringen er nettopp det som gjør
    // at noen finner den igjen senere. Etiketten er sitatet fra da meldingen ble
    // skrevet, så den holder seg lesbar også etter at sporet er sluppet.
    if (this.anker) {
      const boks = div('report-anker');
      boks.append(span('report-anker-merke', `OM ${this.anker.slag.toUpperCase()}`));
      boks.append(span('report-anker-navn', this.anker.etikett || this.anker.id));
      boks.append(span('report-anker-forklaring',
        'Meldingen henges på dette objektet. Neste gang noen åpner det, står samtalen der.'));
      this.form.append(boks);
    }
    const oppg = this.fritekst ? oppgraderFritekst(this.type, this.fritekst) : null;
    if (oppg?.felt) forvalg[oppg.felt] = oppg.tekst;
    if (oppg) {
      // Forklaringen står I SKJEMAET, ikke i en alarm som forsvinner: operatøren
      // skal kunne lese hvor ordene hennes ble av mens hun fyller ut resten.
      const boks = div('report-oppgradert');
      boks.append(span('report-oppgradert-merke', 'FRA «MELD FRA»'));
      boks.append(span('report-oppgradert-forklaring', oppg.forklaring));
      if (!oppg.felt) {
        // ⚠ MEDEVAC-VEIEN. Teksten har ingen plass i formatet, så den vises
        // ordrett her — synlig og markerbar — framfor å bli dyttet inn i en linje
        // den ikke hører til. Den forsvinner ikke; den plasseres ikke.
        const t = document.createElement('textarea');
        t.className = 'report-input report-oppgradert-tekst';
        t.value = oppg.tekst;
        t.readOnly = true;
        t.rows = 2;
        boks.append(t);
      }
      this.form.append(boks);
    }
    const obsFelt = OBSERVASJONSFELT[this.type];
    const tidFelt = TIDSFELT[this.type];
    let tidsvalg: Tidsvalg = null;
    let tidInput: HTMLInputElement | null = null;

    for (const f of REPORT_FIELDS[this.type]) {
      // Arvefelt tilbys ALDRI som inndata. De finnes bare for å VISE innhold som
      // allerede har kommet inn fra en avsender i drift (se `legacy` i
      // @skade/contracts report.ts). Gis de et inndatafelt her, får operatøren
      // tilbake nettopp det doktrinen har fjernet — for SALUTE: to felt for
      // samme bokstav.
      if (f.legacy) continue;

      // ── HVOR (sted/observasjon): egen gruppe med forklaring ────────────────
      if (f.key === obsFelt) {
        const gruppe = div('report-field report-obsgruppe');
        gruppe.append(span('report-field-label', f.label));
        gruppe.append(span('report-gruppeforklaring',
          'Stedet for OBSERVASJONEN — ikke der du står. Avsenderens egen posisjon står i blokka over.'));
        const input = document.createElement('input');
        input.className = 'report-input';
        const v = observasjonsForvalg(this.type, this.targetCoord, k);
        if (v) input.value = v;
        else input.placeholder = 'MGRS eller stedsnavn — eller åpne meldingen på et kartpunkt';
        inputs.set(f.key, input);
        gruppe.append(input);
        this.form.append(gruppe);
        continue;
      }

      // ── NÅR/Tid: obligatorisk «Observert nå / tidligere»-valg ──────────────
      if (f.key === tidFelt) {
        const gruppe = div('report-field report-tidsgruppe');
        gruppe.append(span('report-field-label', f.label));
        const input = document.createElement('input');
        input.className = 'report-input';
        input.disabled = true; // åpnes av valget «tidligere»
        input.placeholder = 'velg «Observert nå» eller «Observert tidligere»';
        const valgRad = div('report-tidsvalg');
        const gruppeNavn = `tidsvalg-${tidsvalgSeq++}`;
        const lagValg = (tekst: string, onValg: () => void) => {
          const lbl = document.createElement('label');
          lbl.className = 'report-tidsvalg-alt';
          const radio = document.createElement('input');
          radio.type = 'radio';
          radio.name = gruppeNavn;
          radio.onchange = onValg;
          lbl.append(radio, document.createTextNode(` ${tekst}`));
          return lbl;
        };
        valgRad.append(
          lagValg('Observert nå (DTG settes ved sending)', () => {
            tidsvalg = 'naa';
            input.disabled = true;
            input.value = buildDtg(this.hentKontekst().naaMs, AUTOFYLL_SONE);
          }),
          lagValg('Observert tidligere — rett DTG', () => {
            tidsvalg = 'tidligere';
            input.disabled = false;
            // Nå-DTG som UTGANGSPUNKT for retting er ærlig her: operatøren har
            // nettopp SAGT at tida er en annen, så feltet står åpent og ventende.
            if (!input.value) input.value = buildDtg(this.hentKontekst().naaMs, AUTOFYLL_SONE);
            input.focus();
          }),
        );
        gruppe.append(valgRad, input);
        tidInput = input; // håndteres i submit via byggTidsverdi, IKKE via inputs
        this.form.append(gruppe);
        continue;
      }

      const wrap = div('report-field');
      wrap.append(span('report-field-label', f.label));
      const input = f.long ? document.createElement('textarea') : document.createElement('input');
      input.className = 'report-input';
      if (f.num && input instanceof HTMLInputElement) {
        // Rent tallfelt: numerisk tastatur på nettbrett (feltet betjenes med
        // hansker), ingen desimaler, ingen negative antall. Skinnene er et hint
        // — sannheten håndheves i `byggFelter()`, som er det sendingen går
        // gjennom. `min`/`step` alene stopper ingenting: nettleseren tillater
        // fortsatt at «-3» står i feltet.
        input.type = 'number';
        input.min = '0';
        input.step = '1';
        input.inputMode = 'numeric';
      }
      const v = forvalg[f.key];
      if (v) input.value = v;
      inputs.set(f.key, input);
      wrap.append(input);
      this.form.append(wrap);
    }

    // Feilboks. Den STÅR i skjemaet (skjult) i stedet for å være en alarm som
    // forsvinner: blokkeres sendingen, skal operatøren kunne lese hvorfor mens
    // han retter feltet.
    const feilboks = span('report-error', '');
    feilboks.hidden = true;

    const submit = document.createElement('button');
    submit.className = 'report-submit';
    submit.textContent = this.targetCoord ? 'Plasser melding' : 'Velg punkt på kartet';
    submit.onclick = () => {
      const raa: Record<string, string> = {};
      for (const [k, el] of inputs) {
        // `type="number"` gir TOM `.value` når nettleseren ikke klarer å tolke
        // det som ble tastet («2,5», «to», «3 stk»). Uten `badInput`-sjekken
        // ville et slikt felt gått videre som «ikke oppgitt» — altså stille tap
        // av et pasienttall operatøren TRODDE han hadde skrevet. Vi sender
        // sentinelen videre og lar `byggFelter()` gi samme feilmelding som for
        // enhver annen ugyldig verdi.
        raa[k] = el instanceof HTMLInputElement && el.validity?.badInput ? UTOLKELIG_TALL : el.value;
      }
      // Tidsvalget er OBLIGATORISK der meldingstypen har et observasjonstidsfelt:
      // uten valg blokkeres sendingen — det er hele poenget med å erstatte den
      // stille forhåndsutfyllingen. «Nå» leser klokka VED SENDING (ikke ved
      // åpning av skjemaet — et skjema kan stå åpent lenge i en TIC).
      const tidsfeil: string[] = [];
      if (tidFelt) {
        const etikett = REPORT_FIELDS[this.type].find((f) => f.key === tidFelt)?.label ?? tidFelt;
        const tid = byggTidsverdi(tidsvalg, this.hentKontekst().naaMs, tidInput?.value ?? '');
        if (tid.ok) raa[tidFelt] = tid.verdi;
        else tidsfeil.push(`«${etikett}» ${tid.grunn}`);
      }
      const svar = byggFelter(this.type, raa);
      const alleFeil = [...tidsfeil, ...svar.feil];
      if (alleFeil.length) {
        feilboks.hidden = false;
        feilboks.textContent = `Kan ikke sende — ${alleFeil.join(' · ')}`;
        return;
      }
      feilboks.hidden = true;
      // Meta-feltene (avsender/enhet/DTG/posisjon) følger ALLTID meldingen, slik
      // at mottakeren vet hvem som sendte og når — uten å slå det opp i wire-
      // konvolutten. Skjemaets egne felt vinner ved navnekollisjon.
      this.onSubmit(this.type, { ...this.blokk.felt(), ...svar.fields }, this.targetCoord, this.anker ?? undefined);
      this.close();
    };

    for (const h of doktrineHint(this.type)) this.form.append(span('report-hint', h));
    this.form.append(feilboks);
    this.form.append(submit);
  }
}

// ── Doktrine-hint ───────────────────────────────────────────────────────────

/**
 * Hint under skjemaet. Listen er kort med vilje: et hint per ting systemet IKKE
 * kan gjøre for operatøren.
 *
 * FUNN (UX-revisjon): her sto tidligere «STEDSNAVN/KODENAVN: skriv med STORE
 * bokstaver» — en instruksjon til brukeren om å gjøre systemets jobb, midt i en
 * melding som skal skrives under tidspress. HIMMELRETNINGER og ordet «IKKE»
 * versaliseres nå automatisk ved sending (`stridsEmphasis`, den samme funksjonen
 * klartekstformatet og HILDR-ordreverket bruker — ingen ny kopi), og for ALLE
 * meldingstyper, ikke bare MELDING som før.
 *
 * STEDSNAVN og KODENAVN står IGJEN i hintet, og det er en bevisst grense, ikke
 * latskap: de kan ikke utledes av teksten. «Kolsås» er et stedsnavn, «kolsås»
 * kan være et stedsnavn, og «Bjørn» kan være et kodenavn, en person eller et
 * dyr. En regel som versaliserte alle ord med stor forbokstav ville gjort
 * «Tropp rykker mot broen» til «TROPP rykker mot broen» og fylt meldingene med
 * feilaktig utheving — utheving som i denne doktrinen BETYR noe. Å gjette der
 * ville vært å ødelegge data for å slippe én instruksjonslinje.
 *
 * Det som mangler for å lukke resten av funnet er et navneregister: en gazetteer
 * over stedsnavn i operasjonsområdet og et register over gjeldende kodenavn, som
 * kan slås opp ord for ord. `kritiske-objekter.gen.ts` har navn på objekter i
 * Oslofjord-området og kunne vært et startpunkt, men hvilke navneklasser som
 * skal versaliseres er en DOKTRINEBESLUTNING for eieren — ikke noe en komponist
 * skal avgjøre på egen hånd.
 */
export function doktrineHint(type: ReportType): string[] {
  const ut: string[] = [];
  if (type === 'MELDING') ut.push('DTG settes automatisk ved sending.');
  if (type === 'SALUTE') {
    // Tid er IKKE lenger stille forhåndsutfylt — den krever et aktivt valg
    // (funn 7). Hintet skal stemme med skjemaet, ellers lærer det operatøren feil.
    ut.push('Sted er forhåndsutfylt fra kartpunktet når meldingen er åpnet på et punkt — RETT det hvis fienden var et annet sted enn markøren. Tid krever valget «Observert nå / tidligere» over.');
  }
  ut.push('HIMMELRETNINGER og «IKKE» versaliseres automatisk. STEDSNAVN og KODENAVN må du skrive med versaler selv — de kan ikke utledes av teksten.');
  return ut;
}

// ── Observasjonssted og -tid (UX-funn 7) ────────────────────────────────────

/**
 * Sonen tid skrives i. `'A'` (ALFA) er valgt for å STEMME med
 * `report-thread.ts`, som viser DTG med samme sone: to ulike soner i komponist og
 * visning ville gitt en melding som endret klokkeslett mellom skriving og
 * lesing. MERK at ALFA er norsk NORMALTID (UTC+1) — om sommeren er BRAVO
 * (UTC+2) den riktige. Sonevalget er ikke løst noe sted i COP i dag; det er
 * flagget, ikke gjettet på.
 */
const AUTOFYLL_SONE: StridsSone = 'A';

/** Unike radiogruppe-navn per skjemategning (to komponister i samme dokument
 * skal ikke dele valg). */
let tidsvalgSeq = 0;

/**
 * Feltet som bærer OBSERVASJONSSTEDET per meldingstype. MELDING (HVOR) og
 * SALUTE (Sted L) deler nå SAMME kartkoblede mønster: kartpunktet vinner,
 * ellers gjelder reglene i `observasjonsForvalg`. Typene uten oppslag har ikke
 * et fritt observasjonssted i doktrinen (MEDEVAC har hentested som egen linje).
 */
export const OBSERVASJONSFELT: Partial<Record<ReportType, string>> = {
  MELDING: 'hvor',
  SALUTE: 'location',
};

/** Feltet som bærer OBSERVASJONSTIDEN per meldingstype — de samme to typene.
 * CBRN `time` er HENDELSES-tid med egen doktrin og beholder prefyll fra DTG. */
export const TIDSFELT: Partial<Record<ReportType, string>> = {
  MELDING: 'naar',
  SALUTE: 'time',
};

/** Merket som skiller «avsenders posisjon gjenbrukt som sted» fra et målt sted.
 * Uten det leses avsenderkoordinatet som observasjonens koordinat (funn 7). */
export const AVSENDER_POS_MERKE = '(AVSENDERS POSISJON — rett hvis observert annet sted)';

/**
 * Forvalget for observasjonsstedet. Ren funksjon → testbar.
 *
 *  * Kartpunkt valgt (meldingen åpnet PÅ et punkt) → punktets MGRS. Punktet er
 *    operatørens eksplisitte stedsvalg og vinner alltid.
 *  * MELDING uten punkt → avsenderposisjonen, men ALLTID MERKET som avsenderens
 *    (`AVSENDER_POS_MERKE`): et umerket koordinat i HVOR leses som målt
 *    observasjonssted, og det er nettopp sammensmeltingen funn 7 påpeker.
 *  * SALUTE uten punkt → TOMT. Alt i en SALUTE beskriver fienden; å fylle L med
 *    egen posisjon ville laget en observasjon av oss selv.
 */
export function observasjonsForvalg(
  type: ReportType,
  coord: [number, number] | null,
  k: Pick<Kontekst, 'posisjonMerket'>,
): string {
  if (!OBSERVASJONSFELT[type]) return '';
  if (coord) {
    // `latLonToMgrs` gir '' utenfor MGRS-sonene i stedet for å kaste. Tomt →
    // ikke noe forvalg, så operatøren ser at han må skrive stedet selv.
    const mgrs = latLonToMgrs({ lat: coord[1], lon: coord[0] });
    return mgrs ? `MGRS ${mgrs}` : '';
  }
  if (type === 'MELDING' && k.posisjonMerket) return `${k.posisjonMerket} ${AVSENDER_POS_MERKE}`;
  return '';
}

export type Tidsvalg = 'naa' | 'tidligere' | null;
export type TidsvalgSvar = { ok: true; verdi: string } | { ok: false; grunn: string };

/**
 * Observasjonstiden som skal sendes, gitt det OBLIGATORISKE valget.
 *
 *  * Ingen valg → sendingen BLOKKERES med en grunn. Det er dette som erstatter
 *    den stille forhåndsutfyllingen: en DTG som bare sto der ble aldri rettet,
 *    og sendetid ble lest som observasjonstid i TOC (funn 7, samme rot som
 *    funn 03 i REPORT_FIELDS.SALUTE).
 *  * «Observert nå» → nå-tid som DTG, lest VED SENDING (`naaMs` sendes inn —
 *    ren funksjon, testbar).
 *  * «Observert tidligere» → operatørens redigerte DTG; tom redigering
 *    blokkeres (å velge «tidligere» og så ikke si når er ikke et tidspunkt).
 */
export function byggTidsverdi(valg: Tidsvalg, naaMs: number, redigert: string): TidsvalgSvar {
  if (valg === 'naa') return { ok: true, verdi: buildDtg(naaMs, AUTOFYLL_SONE) };
  if (valg === 'tidligere') {
    const t = redigert.trim();
    return t ? { ok: true, verdi: t } : { ok: false, grunn: 'skriv DTG for når du observerte' };
  }
  return { ok: false, grunn: 'velg «Observert nå» eller «Observert tidligere»' };
}

// ── Tallvalidering ──────────────────────────────────────────────────────────

/**
 * Sentinel for «nettleseren kunne ikke tolke det som ble tastet i et tallfelt».
 *
 * NUL kan aldri komme fra et `<input>`, og er derfor trygg: ingen operatør kan
 * treffe den ved uhell. Skrevet som escape (`\u0000`), ALDRI som rå byte — en rå
 * NUL i kildefila gjør den «binary» for grep og diff, og en kommentar ingen kan
 * grep-e er en kommentar ingen finner.
 */
export const UTOLKELIG_TALL = '\u0000utolkelig';

export type TallSvar = { ok: true; verdi: string } | { ok: false; grunn: string };

/**
 * Valider et rent tallfelt: ikke-negativt HELTALL, eller tomt.
 *
 * Tomt er lovlig og betyr «ikke oppgitt» — ikke 0. Forskjellen er reell: «0
 * HASTER» er en påstand om at ingen haster, «ikke oppgitt» er at linja ikke er
 * fylt ut. Å normalisere tomt til «0» ville laget pasienttall det ikke finnes
 * grunnlag for.
 *
 * Ugyldig gir en GRUNN, ikke `false`: meldingen skal blokkeres med en tekst
 * operatøren kan handle på, aldri sendes videre som tom streng.
 */
export function validerTallfelt(raa: string): TallSvar {
  if (raa === UTOLKELIG_TALL) return { ok: false, grunn: 'må være et helt tall (0 eller mer)' };
  const t = raa.trim();
  if (t === '') return { ok: true, verdi: '' };
  if (!/^\d+$/.test(t)) {
    // Dekker «-3», «2,5», «2.5», «ca 3», «to», «2-3». Meldingen sier hva som
    // KREVES, ikke bare at det er feil.
    return { ok: false, grunn: 'må være et helt tall (0 eller mer)' };
  }
  const n = Number(t);
  if (!Number.isSafeInteger(n)) return { ok: false, grunn: 'er urimelig stort' };
  return { ok: true, verdi: String(n) }; // «03» → «3»: én skrivemåte i wire-formatet
}

export interface SkjemaSvar {
  ok: boolean;
  /** Feltene som skal sendes (tomme felt utelates, som før). */
  fields: Record<string, string>;
  /** Feilene som BLOKKERER sending. Tom når `ok`. */
  feil: string[];
}

/**
 * Rå skjemaverdier → feltsettet som sendes, eller feilene som stopper sendingen.
 *
 * To ting skjer her, og begge er systemets jobb — ikke operatørens:
 *   1. `num`-felt valideres (`validerTallfelt`). ÉN ugyldig verdi stopper HELE
 *      meldingen. Alternativet — sende resten og droppe det ugyldige feltet —
 *      er nettopp den stille varianten vi har blitt brent av før: en MEDEVAC der
 *      «2» i HASTER ble borte ser ut som en MEDEVAC uten hastepasienter.
 *   2. Fritekst versaliseres etter doktrine (`stridsEmphasis`: HIMMELRETNINGER +
 *      «IKKE»). FØR gjaldt dette bare `MELDING`; en SALUTE med «beveger seg mot
 *      nordvest» fikk ingen utheving, selv om doktrinen er den samme uansett
 *      meldingstype. Tallfelt kjøres IKKE gjennom versalisering — det finnes
 *      ingen himmelretning i et antall.
 */
export function byggFelter(type: ReportType, raa: Record<string, string>): SkjemaSvar {
  const fields: Record<string, string> = {};
  const feil: string[] = [];
  for (const f of REPORT_FIELDS[type]) {
    // Arvefelt har ingen inndata (se `render`), så de skal heller ikke kunne
    // havne i en NY melding — selv om en kaller skulle sende inn en verdi.
    if (f.legacy) continue;
    const verdi = raa[f.key] ?? '';
    if (f.num) {
      const svar = validerTallfelt(verdi);
      if (!svar.ok) { feil.push(`«${f.label}» ${svar.grunn}`); continue; }
      if (svar.verdi !== '') fields[f.key] = svar.verdi;
      continue;
    }
    const v = verdi.trim();
    if (v) fields[f.key] = stridsEmphasis(v);
  }
  return { ok: feil.length === 0, fields, feil };
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
